import { ChevronLeftDouble, ChevronRightDouble } from "@untitledui/icons";
import { Button as AriaButton } from "react-aria-components";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";

interface SidebarToggleProps {
	/**
	 * Whether the sidebar is currently collapsed (icon rail).
	 * When true, shows expand (chevron-right-double); otherwise collapse.
	 */
	isCollapsed?: boolean;
	/** Called when the user activates the toggle. */
	onToggle: () => void;
	/** Accessible + tooltip label (e.g. Collapse / Expand). */
	label: string;
	/** Brand (dark) vs light sidebar chrome. Defaults to brand. */
	variant?: "light" | "brand";
	/** Extra classes on the root button. */
	className?: string;
}

/**
 * Full-width sidebar collapse / expand control (Figma 3970:31741).
 *
 * Brand: `alpha-white-10` bar, centered 24px double chevron, `spacing-sm` vertical padding.
 * No top border — the tinted bar itself separates footer chrome from the rail.
 */
export function SidebarToggle({
	isCollapsed = false,
	onToggle,
	label,
	variant = "brand",
	className,
}: SidebarToggleProps) {
	const isBrand = variant === "brand";
	const Icon = isCollapsed ? ChevronRightDouble : ChevronLeftDouble;

	return (
		<Tooltip title={label} placement="right">
			<AriaButton
				aria-label={label}
				aria-expanded={!isCollapsed}
				onPress={onToggle}
				className={cx(
					"hidden lg:flex w-full shrink-0 cursor-pointer items-center justify-center",
					"py-(--spacing-sm,6px) outline-none transition duration-100 ease-linear",
					"focus-visible:outline-2 focus-visible:-outline-offset-2",
					isBrand
						? [
								"bg-alpha-white-10",
								"text-alpha-white-60 hover:bg-alpha-white-20 hover:text-white",
								"focus-visible:outline-white",
							]
						: [
								"bg-secondary",
								"text-fg-quaternary hover:bg-primary_hover hover:text-fg-quaternary_hover",
								"focus-visible:outline-brand",
							],
					className,
				)}
			>
				<Icon aria-hidden className="size-6 shrink-0" data-icon />
			</AriaButton>
		</Tooltip>
	);
}
